import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, ChevronLeft, Check, Minus, Plus } from "lucide-react";
import api, { formatRON, formatEUR, PLACEHOLDER_IMG } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import OfferButton from "@/components/OfferButton";
import AlertButton from "@/components/AlertButton";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const specLabels = {
  motor_power_kw: "Putere motor",
  spindle_diameter_mm: "Diametru arbore",
  weight_kg: "Greutate",
  max_rpm: "Turație maximă",
  year: "An fabricație",
};
const specUnits = { motor_power_kw: "kW", spindle_diameter_mm: "mm", weight_kg: "kg", max_rpm: "rpm", year: "" };

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => navigate("/produse"));
  }, [id, navigate]);

  useEffect(() => {
    if (!p) return;
    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    const priceTxt = p.price ? `${Math.round(p.price)} RON` : "Preț la cerere";
    const title = `${p.name} · ${priceTxt} | OXO.ro`;
    const desc = (p.description || `${p.name} — utilaj industrial disponibil pe OXO.ro.`).slice(0, 160);
    const prevTitle = document.title;
    document.title = title;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "product");
    setMeta("property", "og:url", window.location.href);
    if (p.image) setMeta("property", "og:image", p.image.startsWith("http") ? p.image : `${process.env.REACT_APP_BACKEND_URL}${p.image}`);
    setMeta("name", "twitter:card", "summary_large_image");
    return () => { document.title = prevTitle; };
  }, [p]);

  if (!p) return <div className="p-24 text-center text-slate-400">{t("pd.loading")}</div>;
  const specs = Object.entries(p.specs || {}).filter(([, v]) => v != null);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <Link to="/produse" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-orange-600 mb-8 transition-colors" data-testid="back-link">
        <ChevronLeft className="w-4 h-4" /> {t("pd.back")}
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="border border-slate-200 bg-slate-100 aspect-square overflow-hidden">
          <img src={p.image || PLACEHOLDER_IMG} alt={p.name} className="w-full h-full object-cover" data-testid="product-image"
            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }} />
        </div>
        <div>
          {p.brand && <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-600">{p.brand}</span>}
          <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-slate-900 mt-3" data-testid="product-name">{p.name}</h1>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-3xl font-black text-slate-900" data-testid="product-price">{p.price ? formatRON(p.price) : t("pd.priceOnRequest")}</span>
            {p.price_eur ? <span className="text-base text-slate-400 font-mono" data-testid="product-price-eur">≈ {formatEUR(p.price_eur)}</span> : null}
            {p.stock > 0 ? (
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-600"><Check className="w-4 h-4" /> {t("pd.inStock")} ({p.stock})</span>
            ) : (
              <span className="text-sm font-semibold text-red-600">{t("pd.outOfStock")}</span>
            )}
          </div>
          <p className="text-slate-600 leading-relaxed mt-6">{p.description}</p>

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-slate-300">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-slate-100" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
              <span className="w-12 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-slate-100" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => { if (!p.price) { toast.info(t("pd.priceOnRequestToast")); return; } addItem(p, qty); toast.success(t("pd.addedToCart")); }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-bold uppercase tracking-wide text-sm transition-colors"
              data-testid="add-to-cart-detail">
              <ShoppingCart className="w-5 h-5" /> {t("pd.addToCart")}
            </button>
          </div>

          <div className="mt-4 max-w-xs">
            <OfferButton product={p} size="lg" />
          </div>

          <div className="mt-3 max-w-xs">
            <AlertButton
              variant="outline"
              className="w-full"
              label={t("alert.similar")}
              criteria={{ category: p.category || "", keyword: "", condition: "" }}
              testid="product-alert-subscribe"
            />
          </div>

          {specs.length > 0 && (
            <div className="mt-10 border border-slate-200">
              <div className="bg-slate-900 text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold">{t("pd.specs")}</div>
              <table className="w-full text-sm">
                <tbody>
                  {specs.map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-slate-50" : ""} data-testid={`spec-${k}`}>
                      <td className="px-5 py-3 text-slate-500">{specLabels[k] || k}</td>
                      <td className="px-5 py-3 font-mono font-semibold text-slate-900 text-right">{v} {specUnits[k]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {user?.role === "admin" && p.private_data && (
            <div className="mt-8 border-2 border-red-500 bg-red-50/60" data-testid="admin-private-panel">
              <div className="bg-red-600 text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
                🔒 Date interne (doar administrator)
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {[
                    ["source_url", "Sursă (URL)"],
                    ["original_owner_phone", "Telefon proprietar"],
                    ["purchase_price", "Preț achiziție"],
                    ["internal_notes", "Note interne"],
                  ].map(([k, label], i) => (
                    <tr key={k} className={i % 2 ? "bg-red-100/40" : ""} data-testid={`private-${k}`}>
                      <td className="px-5 py-2.5 text-red-700/80 whitespace-nowrap align-top">{label}</td>
                      <td className="px-5 py-2.5 font-mono text-red-900 break-all">
                        {p.private_data[k] != null && p.private_data[k] !== "" ? String(p.private_data[k]) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
